import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import type { VehicleCategory } from "../../types/vehicleCategory";

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

interface VehicleCategoryModalProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit" | "delete";
  category?: VehicleCategory | null;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
}

export const VehicleCategoryModal: React.FC<VehicleCategoryModalProps> = ({
  open,
  onClose,
  mode,
  category,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    category_id: "",
    category_name: "",
    marker_image: "",
    color: "",
    max_speed_kmh: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (mode === "create") {
        setFormData({
          category_id: "",
          category_name: "",
          marker_image: "",
          color: "",
          max_speed_kmh: "",
        });
      } else if (category) {
        setFormData({
          category_id: category.category_id,
          category_name: category.category_name,
          marker_image: category.marker_image ?? "",
          color: category.color ?? "",
          max_speed_kmh:
            category.max_speed_kmh !== undefined &&
            category.max_speed_kmh !== null
              ? String(category.max_speed_kmh)
              : "",
        });
      }
      setError("");
    }
  }, [open, mode, category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (mode !== "delete") {
      if (!formData.category_id.trim()) {
        setError("Category ID is required");
        return;
      }
      if (!formData.category_name.trim()) {
        setError("Category name is required");
        return;
      }
      if (
        formData.marker_image.trim() &&
        !/^https?:\/\/.+/i.test(formData.marker_image.trim())
      ) {
        setError("Marker image must be a valid HTTP(S) URL");
        return;
      }
      if (
        formData.color.trim() &&
        !HEX_COLOR_PATTERN.test(formData.color.trim())
      ) {
        setError("Color must be a valid hex value (#RRGGBB)");
        return;
      }
      if (
        formData.max_speed_kmh.trim() &&
        (!Number.isFinite(Number(formData.max_speed_kmh)) ||
          Number(formData.max_speed_kmh) < 0)
      ) {
        setError("Max speed must be a non-negative number");
        return;
      }
    }
    setLoading(true);
    try {
      if (mode === "delete") {
        await onSave({});
      } else {
        await onSave({
          category_id: formData.category_id.trim().toUpperCase(),
          category_name: formData.category_name.trim(),
          marker_image: formData.marker_image.trim() || null,
          color: formData.color.trim() || null,
          max_speed_kmh: formData.max_speed_kmh.trim()
            ? Number(formData.max_speed_kmh)
            : null,
        });
      }
      onClose();
    } catch (submitError) {
      setError(
        submitError instanceof Error ? submitError.message : "Failed to save",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        />
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>
        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {mode === "create"
                  ? "Add Vehicle Category"
                  : mode === "edit"
                    ? "Edit Vehicle Category"
                    : "Delete Vehicle Category"}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                {error}
              </div>
            )}

            {mode === "delete" ? (
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100 sm:mx-0">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div className="mt-2 sm:mt-0 sm:ml-4 text-sm text-gray-600">
                  Delete vehicle category{" "}
                  <strong>{category?.category_name}</strong> (
                  {category?.category_id})?
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Category ID
                  </label>
                  <input
                    value={formData.category_id}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        category_id: e.target.value.toUpperCase(),
                      }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="EV-01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Category name
                  </label>
                  <input
                    value={formData.category_name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        category_name: e.target.value,
                      }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Electric vehicle bikes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Marker image URL (optional)
                  </label>
                  <input
                    value={formData.marker_image}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        marker_image: e.target.value,
                      }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="https://example.com/marker.png"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Marker fallback color (optional)
                  </label>
                  <div className="mt-1 flex items-center gap-3">
                    <input
                      type="color"
                      value={
                        HEX_COLOR_PATTERN.test(formData.color)
                          ? formData.color
                          : "#3b82f6"
                      }
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          color: e.target.value.toUpperCase(),
                        }))
                      }
                      className="h-9 w-12 border border-gray-300 rounded-md bg-white p-1"
                    />
                    <input
                      value={formData.color}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          color: e.target.value.toUpperCase(),
                        }))
                      }
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
                      placeholder="#3B82F6"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Used when marker image is empty. Format: #RRGGBB
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Max speed (km/h, optional)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={formData.max_speed_kmh}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        max_speed_kmh: e.target.value,
                      }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="25"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Used for GPS outlier detection. Per-bike max speed overrides
                    this value.
                  </p>
                </div>
              </form>
            )}
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`inline-flex justify-center py-2 px-4 text-sm font-medium rounded-md text-white ${
                mode === "delete"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-blue-600 hover:bg-blue-700"
              } disabled:opacity-50`}
            >
              {loading
                ? "Processing..."
                : mode === "create"
                  ? "Add Category"
                  : mode === "edit"
                    ? "Save Changes"
                    : "Delete Category"}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="mt-3 sm:mt-0 sm:mr-3 inline-flex justify-center py-2 px-4 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
